/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
import {
  Building2,
  Calculator,
  CheckCircle,
  Clock,
  DollarSign,
  FileText,
  Layers,
  Truck,
  Wrench,
  X,
} from "lucide-react";
import type { SolutionTemplateConfig } from "@/components/landing/solution-template";
import {
  CTA,
  AI_TOOL_COUNT,
  STARTING_PRICE,
} from "@/content/landing/constants";

export const generalContractingConfig: SolutionTemplateConfig = {
  tradeName: "General Contracting",
  tradeSlug: "general-contracting",
  hero: {
    badge: "GC Business Platform",
    title: "Software for general contractors",
    titleHighlight: "from pre-con to punch list.",
    description:
      "What's your real markup on subs? Which trade is over budget? Did the scope gap between plumbing and electrical cost you money? One platform for sub bids, self-perform work, project tracking, and invoicing.",
    ctaText: CTA.PRIMARY,
    trustIndicators: [
      "7-day free trial",
      "No long-term contracts",
      "Multi-trade support",
      "Cancel anytime",
    ],
  },
  trustBar: {
    headline: "Built for general contractors",
    proofPoints: [
      { icon: Building2, text: "Sub bids and self-perform in one estimate" },
      { icon: DollarSign, text: "Markup on subs tracked per trade" },
      { icon: FileText, text: "Scope gap detection across trades" },
    ],
  },
  painPoints: {
    title: "Does this sound familiar?",
    description: "The daily reality of running a GC business without a system.",
    items: [
      {
        icon: Clock,
        title: "Juggling sub bids across 10 trades",
        description:
          "Electrical sub sent a PDF. Plumbing sub sent a spreadsheet. HVAC sub sent a number in a text. You're assembling the bid from three different formats and hoping nothing fell through the cracks.",
      },
      {
        icon: DollarSign,
        title: "Your markup on subs isn't tracked",
        description:
          "You mark up sub bids 10-20% but you don't know if your markup covers your actual overhead on each trade. Some trades are profitable, some aren't — you just don't know which.",
      },
      {
        icon: FileText,
        title: "Scope gaps between trades kill your margin",
        description:
          "The plumber assumed the electrician was running the water heater circuit. The electrician assumed it was in the plumbing scope. Nobody caught it until the job was underway. That gap came out of your pocket.",
      },
    ],
  },
  stats: [
    { value: "Multi-trade", label: "Subs + self-perform in one estimate" },
    { value: `${AI_TOOL_COUNT} tools`, label: "AI assistant for your data" },
    { value: `$${STARTING_PRICE}/mo`, label: "Starting price" },
  ],
  howItWorks: {
    title: "The full lifecycle — one platform",
    description: "From sub bids to owner close-out.",
    steps: [
      {
        step: 1,
        title: "Set up your business",
        description:
          "Configure your self-perform labor and material costs. Track subcontractors through supplier management with contact info, pricing history, and contract terms. Set your markup rates per trade.",
        icon: Building2,
      },
      {
        step: 2,
        title: "Win the work",
        description:
          "Import sub bids from Excel, organize by CSI division or trade, and add your self-perform work. Scope tab documents what's included and excluded per trade — catch gaps before they cost you. Roll everything up with your markup into one owner proposal.",
        icon: Calculator,
      },
      {
        step: 3,
        title: "Run the job",
        description:
          "Track budget vs. actuals across every trade. Log change orders. Monitor sub costs against their bid amounts. Time entries for your self-perform crews. Know which trades are on budget and which aren't.",
        icon: Wrench,
      },
      {
        step: 4,
        title: "Know your numbers",
        description:
          "See your real markup per trade across all jobs. Compare sub pricing history. Ask Hank: 'Which electrical subs came in under budget this year?'",
        icon: DollarSign,
      },
    ],
  },
  features: {
    title: "What you actually get",
    description:
      "Not a specialty trade tool. A platform that handles multi-trade coordination, sub management, and the complexity of general contracting.",
    items: [
      {
        icon: Layers,
        title: "Multi-trade estimates",
        description:
          "Organize by CSI division or trade. Roll up sub bids alongside self-perform work. Each trade gets its own work area with separate markup, labor rates, and material costs.",
        bullets: [
          "Sub bids + self-perform in one estimate",
          "Per-trade markup (10-20% standard)",
          "CSI division organization",
          "Change order tracking",
        ],
      },
      {
        icon: Truck,
        title: "Subcontractor management",
        description:
          "Track every sub — contact info, pricing history, contract terms, insurance dates. 5-tab detail page: overview, contact, catalog, financial, and contract with expiry alerts.",
        bullets: [
          "Sub directory with full detail pages",
          "Pricing history across jobs",
          "Contract tracking with expiry alerts",
          "Insurance and license tracking",
        ],
      },
      {
        icon: FileText,
        title: "Scope documentation",
        description:
          "The scope tab on every estimate documents what's included and excluded. When scope gaps appear between trades, you catch them in the estimate — not on the job site.",
        bullets: [
          "Per-estimate scope editor",
          "Import scope from RFPs and contracts",
          "AI scope extraction from documents",
          "Exclusions and clarifications documented",
        ],
      },
      {
        icon: DollarSign,
        title: "Budget tracking across trades",
        description:
          "Track actual costs against every sub bid and self-perform line item. See which trades are over budget, which are under, and where the money went.",
        bullets: [
          "Per-trade budget vs. actuals",
          "Sub cost tracking against bid amounts",
          "Self-perform labor and material tracking",
          "Project-level financial rollup",
        ],
      },
    ],
  },
  beforeAfter: {
    title: "The old way vs. the CC way",
    before: [
      { icon: X, text: "Sub bids in three different formats" },
      { icon: X, text: "Markup not tracked per trade" },
      { icon: X, text: "Scope gaps discovered on the job site" },
      { icon: X, text: "No visibility into which trades are over budget" },
      { icon: X, text: "Owner proposals assembled from pieces" },
      { icon: X, text: "Change orders tracked on email" },
    ],
    after: [
      { icon: CheckCircle, text: "All sub bids in one organized estimate" },
      { icon: CheckCircle, text: "Markup tracked per trade across all jobs" },
      { icon: CheckCircle, text: "Scope documentation catches gaps early" },
      { icon: CheckCircle, text: "Budget vs. actuals by trade in real time" },
      { icon: CheckCircle, text: "Professional owner proposals in one click" },
      { icon: CheckCircle, text: "Change orders tracked against budget" },
    ],
  },
  roi: {
    title: "What changes when your business is connected",
    description:
      "When sub bids feed into estimates, scope is documented per trade, and budget tracking runs across every division.",
    metrics: [
      { value: "Organized", label: "Every trade in one estimate, one project" },
      { value: "Protected", label: "Scope gaps caught before they cost you" },
      { value: "Visible", label: "Real markup per trade across all jobs" },
    ],
    ctaText: CTA.PRIMARY,
  },
  faq: {
    title: "Questions GCs ask",
    items: [
      {
        question: "Can I import sub bids?",
        answer:
          "Yes. Import from Excel or CSV. Organize by CSI division or trade. Add your markup on top. Mix sub bids with self-perform work in the same estimate.",
      },
      {
        question: "Does it track my markup per trade?",
        answer:
          "Yes. Set markup rates per trade. Track actual costs against the marked-up bid. See which trades are most profitable across all your jobs.",
      },
      {
        question: "How does scope documentation work?",
        answer:
          "Every estimate has a scope tab with a rich text editor. Document inclusions and exclusions per trade. Import scope from uploaded RFPs. AI can extract scope from contract documents.",
      },
      {
        question: "Can I track subs across jobs?",
        answer:
          "Yes. Each sub has a 5-tab detail page — overview, contact, catalog, financial, and contracts. See their pricing history and compare performance across jobs.",
      },
      {
        question: "Can I track budget vs. actuals by trade?",
        answer:
          "Yes. Log actual costs against estimate line items organized by trade. See per-trade variance in real time.",
      },
      {
        question: "Can my team use it?",
        answer:
          "Yes. Add PMs, estimators, or office staff. Role-based access. Division management on the Business plan.",
      },
    ],
  },
  cta: {
    title: "Run your GC business from one platform.",
    description:
      "Sub bids, scope documentation, multi-trade tracking, invoicing — connected. 7-day free trial.",
    ctaText: CTA.PRIMARY,
    trustIndicators: [
      "7-day free trial",
      "No long-term contracts",
      "Multi-trade support",
      "Cancel anytime",
    ],
  },
};
